import React, { useState, useEffect, useRef, useCallback } from 'react';

/**
 * LiveOCRScanner – Full-screen camera overlay with live Tesseract OCR
 * AND Gemini Vision AI for high-accuracy serial/product-code detection.
 *
 * Props:
 *   onConfirm(text)   – called when user accepts a detected text
 *   onCancel()        – called when user cancels
 *   onAIScan(blob)    – optional; if provided, "🔍 AI Detect" button appears.
 *                       Should return Promise<{ serials, raw_text, error }>.
 *   darkMode          – boolean
 *   halfScreen        – boolean (render as bottom sheet)
 */

const LiveOCRScanner = ({ onConfirm, onCancel, onAIScan, darkMode, halfScreen = false }) => {
  const videoRef     = useRef(null);
  const canvasRef    = useRef(null);
  const overlayRef   = useRef(null);
  const streamRef    = useRef(null);
  const workerRef    = useRef(null);
  const ocrTimerRef  = useRef(null);
  const animFrameRef = useRef(null);
  const scanningRef  = useRef(false);
  const isMountedRef = useRef(true);

  const [cameraReady, setCameraReady] = useState(false);
  const [ocrText,     setOcrText]     = useState('');
  const [scanning,    setScanning]    = useState(false);
  const [error,       setError]       = useState(null);
  const [confirmed,   setConfirmed]   = useState(false);

  const [selBox,   setSelBox]   = useState(null);
  const [drawing,  setDrawing]  = useState(false);
  const drawStart = useRef(null);

  // AI Detection state
  const [aiLoading,  setAiLoading]  = useState(false);
  const [aiSerials,  setAiSerials]  = useState(null);
  const [aiRawText,  setAiRawText]  = useState('');
  const [aiError,    setAiError]    = useState(null);
  const [activeTab,  setActiveTab]  = useState('live'); // 'live' | 'ai'

  // ── Coord helpers ─────────────────────────────
  const videoToCanvas = useCallback((vx, vy) => {
    const video = videoRef.current;
    const overlay = overlayRef.current;
    if (!video || !overlay) return { x: 0, y: 0 };
    return { x: (vx / (video.videoWidth || 1)) * overlay.width, y: (vy / (video.videoHeight || 1)) * overlay.height };
  }, []);

  const screenToVideo = useCallback((sx, sy) => {
    const overlay = overlayRef.current;
    const video   = videoRef.current;
    if (!overlay || !video) return { x: 0, y: 0 };
    const rect = overlay.getBoundingClientRect();
    return {
      x: ((sx - rect.left) / rect.width)  * (video.videoWidth || 1),
      y: ((sy - rect.top)  / rect.height) * (video.videoHeight || 1),
    };
  }, []);

  // ── Start camera ─────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          setError('Camera not supported on this browser. Please use HTTPS or a modern browser.');
          return;
        }
        let stream;
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } },
            audio: false,
          });
        } catch {
          stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' }, audio: false });
        }
        if (cancelled) { stream.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = stream;
        const video = videoRef.current;
        if (video) {
          video.setAttribute('autoplay', '');
          video.setAttribute('playsinline', '');
          video.setAttribute('muted', '');
          video.setAttribute('webkit-playsinline', '');
          video.srcObject = stream;
          video.onloadedmetadata = () =>
            video.play()
              .then(() => setCameraReady(true))
              .catch(() => setError('Could not start camera playback. Please try again.'));
        }
      } catch (err) {
        if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError')
          setError('Camera permission denied. Please allow camera access in your browser settings and reload.');
        else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError')
          setError('No camera found on this device.');
        else if (err.name === 'NotReadableError' || err.name === 'TrackStartError')
          setError('Camera is in use by another app. Please close other apps using the camera and try again.');
        else
          setError('Could not access camera: ' + (err.message || 'Unknown error'));
      }
    })();
    return () => {
      cancelled = true;
      isMountedRef.current = false;
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    };
  }, []);

  // ── Default guide box ─────────────────────────
  useEffect(() => {
    if (!cameraReady || selBox) return;
    const video = videoRef.current;
    if (!video) return;
    const vw = video.videoWidth, vh = video.videoHeight;
    const bw = vw * 0.6, bh = vh * 0.15;
    setSelBox({ x: (vw - bw) / 2, y: (vh - bh) / 2, w: bw, h: bh });
  }, [cameraReady, selBox]);

  // ── Init Tesseract ────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { createWorker } = await import('tesseract.js');
        const worker = await createWorker('eng');
        if (cancelled) { await worker.terminate(); return; }
        workerRef.current = worker;
      } catch (err) {
        if (!cancelled) setError('OCR engine failed to load: ' + (err.message || err));
      }
    })();
    return () => {
      cancelled = true;
      if (workerRef.current) { workerRef.current.terminate().catch(() => {}); workerRef.current = null; }
    };
  }, []);

  // ── Resize overlay canvas ─────────────────────
  useEffect(() => {
    if (!cameraReady) return;
    const resize = () => {
      const video = videoRef.current, overlay = overlayRef.current;
      if (!video || !overlay) return;
      const rect = video.getBoundingClientRect();
      overlay.width = rect.width; overlay.height = rect.height;
      overlay.style.width = rect.width + 'px'; overlay.style.height = rect.height + 'px';
    };
    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, [cameraReady]);

  // ── Draw overlay continuously ──────────────────
  useEffect(() => {
    if (!cameraReady) return;
    const draw = () => {
      const overlay = overlayRef.current, video = videoRef.current;
      if (!overlay || !video) return;
      const ctx = overlay.getContext('2d');
      ctx.clearRect(0, 0, overlay.width, overlay.height);

      if (selBox) {
        const tl = videoToCanvas(selBox.x, selBox.y);
        const br = videoToCanvas(selBox.x + selBox.w, selBox.y + selBox.h);
        const bw = br.x - tl.x, bh = br.y - tl.y;

        // Dim outside
        ctx.fillStyle = 'rgba(0,0,0,0.45)';
        ctx.fillRect(0, 0, overlay.width, tl.y);
        ctx.fillRect(0, br.y, overlay.width, overlay.height - br.y);
        ctx.fillRect(0, tl.y, tl.x, bh);
        ctx.fillRect(br.x, tl.y, overlay.width - br.x, bh);

        // Box border (animated dash)
        ctx.strokeStyle = '#22d3ee'; ctx.lineWidth = 2.5;
        ctx.setLineDash([8, 4]); ctx.lineDashOffset = -(Date.now() / 60 % 24);
        ctx.strokeRect(tl.x, tl.y, bw, bh); ctx.setLineDash([]);

        // Corners
        const cornerL = Math.min(18, bw / 4, bh / 4);
        ctx.strokeStyle = '#22d3ee'; ctx.lineWidth = 3.5;
        ctx.beginPath(); ctx.moveTo(tl.x, tl.y + cornerL); ctx.lineTo(tl.x, tl.y); ctx.lineTo(tl.x + cornerL, tl.y); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(br.x - cornerL, tl.y); ctx.lineTo(br.x, tl.y); ctx.lineTo(br.x, tl.y + cornerL); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(tl.x, br.y - cornerL); ctx.lineTo(tl.x, br.y); ctx.lineTo(tl.x + cornerL, br.y); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(br.x - cornerL, br.y); ctx.lineTo(br.x, br.y); ctx.lineTo(br.x, br.y - cornerL); ctx.stroke();

        // Tesseract text above box (only in live tab)
        if (ocrText && activeTab === 'live') {
          const textY = Math.max(tl.y - 12, 20);
          const fontSize = Math.max(14, Math.min(22, bw / 15));
          ctx.font = `bold ${fontSize}px monospace`;
          const tm = ctx.measureText(ocrText);
          const px = 10, py = 5;
          ctx.fillStyle = 'rgba(0,0,0,0.75)';
          roundRect(ctx, tl.x + (bw - tm.width) / 2 - px, Math.max(0, textY - fontSize - py), tm.width + px * 2, fontSize + py * 2, 6);
          ctx.fill();
          ctx.fillStyle = '#22d3ee'; ctx.textAlign = 'center';
          ctx.fillText(ocrText, tl.x + bw / 2, textY);
          ctx.textAlign = 'start';
        }

        // Instruction at bottom of box
        const instY = br.y + 28;
        if (instY < overlay.height - 10) {
          ctx.font = '12px sans-serif'; ctx.fillStyle = 'rgba(255,255,255,0.7)'; ctx.textAlign = 'center';
          ctx.fillText(scanning ? '⏳ Reading text…' : 'Draw a box over the serial number · tap AI Detect for best accuracy', tl.x + bw / 2, instY);
          ctx.textAlign = 'start';
        }
      }
      animFrameRef.current = requestAnimationFrame(draw);
    };
    animFrameRef.current = requestAnimationFrame(draw);
    return () => { if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current); };
  }, [cameraReady, selBox, ocrText, scanning, videoToCanvas, activeTab]);

  // ── Periodic Tesseract OCR ────────────────────
  useEffect(() => {
    if (!cameraReady || !selBox || confirmed) return;
    const runOCR = async () => {
      if (!workerRef.current || !videoRef.current || !canvasRef.current) return;
      if (scanningRef.current) return;
      const video = videoRef.current, canvas = canvasRef.current;
      const { x, y, w, h } = selBox;
      if (w < 10 || h < 10) return;
      canvas.width = Math.round(w); canvas.height = Math.round(h);
      const ctx = canvas.getContext('2d');
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(video, x, y, w, h, 0, 0, canvas.width, canvas.height);
      try {
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        for (let i = 0; i < data.length; i += 4) {
          const gray = 0.299 * data[i] + 0.587 * data[i+1] + 0.114 * data[i+2];
          const c = ((gray / 255 - 0.5) * 1.9 + 0.5) * 255;
          const v = Math.max(0, Math.min(255, c));
          data[i] = data[i+1] = data[i+2] = v;
        }
        ctx.putImageData(imageData, 0, 0);
      } catch {}
      scanningRef.current = true; setScanning(true);
      try {
        const { data: { text } } = await workerRef.current.recognize(canvas);
        if (isMountedRef.current) {
          const cleaned = text.replace(/\n/g, ' ').replace(/\s+/g, ' ').trim();
          if (cleaned) setOcrText(cleaned);
        }
      } catch {}
      scanningRef.current = false;
      if (isMountedRef.current) setScanning(false);
    };
    runOCR();
    ocrTimerRef.current = setInterval(runOCR, 1500);
    return () => { if (ocrTimerRef.current) clearInterval(ocrTimerRef.current); };
  }, [cameraReady, selBox, confirmed]);

  // ── Pointer handlers ──────────────────────────
  const handlePointerDown = useCallback((e) => {
    if (confirmed) return;
    e.preventDefault();
    const pt = getPointerPos(e);
    drawStart.current = screenToVideo(pt.x, pt.y);
    setDrawing(true);
    setOcrText(''); setAiSerials(null); setAiRawText(''); setAiError(null); setActiveTab('live');
  }, [confirmed, screenToVideo]);

  const handlePointerMove = useCallback((e) => {
    if (!drawing || !drawStart.current) return;
    e.preventDefault();
    const pt = getPointerPos(e), vPt = screenToVideo(pt.x, pt.y), s = drawStart.current;
    const video = videoRef.current;
    if (!video) return;
    const vw = video.videoWidth, vh = video.videoHeight;
    const x = Math.max(0, Math.min(s.x, vPt.x));
    const y = Math.max(0, Math.min(s.y, vPt.y));
    setSelBox({ x, y, w: Math.min(Math.abs(vPt.x - s.x), vw - x), h: Math.min(Math.abs(vPt.y - s.y), vh - y) });
  }, [drawing, screenToVideo]);

  const handlePointerUp = useCallback(() => { setDrawing(false); drawStart.current = null; }, []);

  // ── AI Detect (Gemini Vision) ──────────────────
  const handleAIDetect = useCallback(async () => {
    if (!onAIScan || aiLoading) return;
    const video = videoRef.current, canvas = canvasRef.current;
    if (!video || !canvas) return;

    let sx = 0, sy = 0, sw = video.videoWidth, sh = video.videoHeight;
    if (selBox && selBox.w > 20 && selBox.h > 20) {
      sx = selBox.x; sy = selBox.y; sw = selBox.w; sh = selBox.h;
    }
    const scale = Math.min(2, 1200 / Math.max(sw, sh));
    canvas.width = Math.round(sw * scale); canvas.height = Math.round(sh * scale);
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(video, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
    try {
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const d = imageData.data;
      for (let i = 0; i < d.length; i += 4) {
        const gray = 0.299 * d[i] + 0.587 * d[i+1] + 0.114 * d[i+2];
        const c = ((gray / 255 - 0.5) * 2.0 + 0.5) * 255;
        const v = Math.max(0, Math.min(255, c));
        d[i] = d[i+1] = d[i+2] = v;
      }
      ctx.putImageData(imageData, 0, 0);
    } catch {}

    canvas.toBlob(async (blob) => {
      if (!blob) return;
      setAiLoading(true); setAiError(null); setActiveTab('ai');
      try {
        const result = await onAIScan(blob);
        if (isMountedRef.current) {
          setAiSerials(result.serials || []);
          setAiRawText(result.raw_text || '');
          if (result.error) setAiError(result.error);
        }
      } catch (err) {
        if (isMountedRef.current) setAiError('AI scan failed: ' + (err.message || err));
      }
      if (isMountedRef.current) setAiLoading(false);
    }, 'image/jpeg', 0.92);
  }, [onAIScan, aiLoading, selBox]);

  // ── Confirm / Reset ───────────────────────────
  const handleConfirm = (text) => {
    const t = (text || (activeTab === 'ai' && aiSerials?.length ? aiSerials[0] : ocrText) || '').trim();
    if (t) { setConfirmed(true); onConfirm(t); }
  };

  const handleReset = () => {
    setOcrText(''); setSelBox(null); setConfirmed(false);
    setAiSerials(null); setAiRawText(''); setAiError(null); setActiveTab('live');
  };

  // ── Error state ───────────────────────────────
  if (error) {
    return (
      <div style={halfScreen ? styles.halfOverlay : styles.overlay}>
        <div style={{ ...styles.errorCard, background: darkMode ? '#1e293b' : '#fff' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📷</div>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8, color: darkMode ? '#f1f5f9' : '#1e293b' }}>Camera Error</div>
          <div style={{ fontSize: 13, color: '#94a3b8', marginBottom: 16 }}>{error}</div>
          <button onClick={onCancel} style={styles.btnCancel}>Close</button>
        </div>
      </div>
    );
  }

  const bestText = activeTab === 'ai' && aiSerials?.length ? aiSerials[0] : ocrText;
  const canConfirm = !!bestText && !aiLoading && !scanning;

  return (
    <div style={halfScreen ? styles.halfOverlay : styles.overlay}>
      <video ref={videoRef} autoPlay playsInline muted style={styles.video} />

      <canvas
        ref={overlayRef}
        style={styles.overlayCanvas}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onPointerLeave={handlePointerUp}
      />
      <canvas ref={canvasRef} style={{ display: 'none' }} />

      {!cameraReady && (
        <div style={styles.loading}>
          <div style={styles.spinner} />
          <div style={{ color: '#fff', marginTop: 12, fontSize: 14 }}>Starting camera…</div>
        </div>
      )}

      {/* Top bar */}
      <div style={styles.topBar}>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#fff', textShadow: '0 1px 4px rgba(0,0,0,0.5)' }}>
          📷 Serial Number Scanner
        </div>
        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.75)', marginTop: 3 }}>
          Draw a box over the serial number · tap <span style={{ color: '#a78bfa', fontWeight: 700 }}>🔍 AI Detect</span> for best accuracy
        </div>
      </div>

      {/* Bottom bar */}
      <div style={styles.bottomBar}>

        {/* Tab switcher */}
        {(ocrText || aiSerials !== null) && (
          <div style={{ display: 'flex', gap: 4, marginBottom: 2 }}>
            <button onClick={() => setActiveTab('live')}
              style={{ ...styles.tabBtn, background: activeTab === 'live' ? 'rgba(34,211,238,0.22)' : 'rgba(255,255,255,0.08)', borderColor: activeTab === 'live' ? '#22d3ee' : 'transparent', color: activeTab === 'live' ? '#22d3ee' : 'rgba(255,255,255,0.55)' }}>
              📝 Tesseract OCR
            </button>
            {aiSerials !== null && (
              <button onClick={() => setActiveTab('ai')}
                style={{ ...styles.tabBtn, background: activeTab === 'ai' ? 'rgba(167,139,250,0.22)' : 'rgba(255,255,255,0.08)', borderColor: activeTab === 'ai' ? '#a78bfa' : 'transparent', color: activeTab === 'ai' ? '#a78bfa' : 'rgba(255,255,255,0.55)' }}>
                🔍 Gemini AI {aiSerials.length > 0 ? `(${aiSerials.length})` : ''}
              </button>
            )}
          </div>
        )}

        {/* Tesseract live result */}
        {activeTab === 'live' && ocrText && (
          <div style={styles.textDisplay}>
            <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 4, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>
              {scanning ? '⏳ Reading…' : '✅ Tesseract Detected'}
            </div>
            <div style={{ fontSize: 16, fontFamily: 'monospace', fontWeight: 700, color: '#22d3ee', wordBreak: 'break-all' }}>
              {ocrText}
            </div>
          </div>
        )}

        {/* AI loading indicator */}
        {aiLoading && (
          <div style={{ ...styles.textDisplay, borderColor: 'rgba(167,139,250,0.4)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 18, height: 18, border: '2.5px solid rgba(167,139,250,0.3)', borderTopColor: '#a78bfa', borderRadius: '50%', animation: 'liveocrSpin 0.8s linear infinite', flexShrink: 0 }} />
              <span style={{ color: '#a78bfa', fontSize: 13, fontWeight: 600 }}>Gemini AI analysing image…</span>
            </div>
          </div>
        )}

        {/* AI results panel */}
        {activeTab === 'ai' && !aiLoading && aiSerials !== null && (
          <div style={{ ...styles.textDisplay, borderColor: 'rgba(167,139,250,0.35)' }}>
            {aiError && <div style={{ fontSize: 12, color: '#f87171', marginBottom: 6 }}>⚠️ {aiError}</div>}
            {aiSerials.length > 0 ? (
              <>
                <div style={{ fontSize: 10, color: '#a78bfa', marginBottom: 6, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>
                  🔍 Gemini AI — {aiSerials.length} code{aiSerials.length > 1 ? 's' : ''} detected
                </div>
                {aiSerials.map((s, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: i < aiSerials.length - 1 ? 6 : 0 }}>
                    <div style={{ fontSize: i === 0 ? 16 : 14, fontFamily: 'monospace', fontWeight: 700, color: '#a78bfa', wordBreak: 'break-all', flex: 1 }}>
                      {i === 0 && <span style={{ fontSize: 9, background: '#a78bfa', color: '#0f172a', borderRadius: 3, padding: '1px 5px', marginRight: 6, verticalAlign: 'middle' }}>BEST</span>}
                      {s}
                    </div>
                    <button onClick={() => handleConfirm(s)}
                      style={{ padding: '5px 12px', background: '#a78bfa', color: '#0f172a', border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 11, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                      Use
                    </button>
                  </div>
                ))}
                {aiRawText && (
                  <details style={{ marginTop: 8 }}>
                    <summary style={{ fontSize: 10, color: '#64748b', cursor: 'pointer', userSelect: 'none' }}>📄 Show all label text</summary>
                    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4, whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontFamily: 'monospace', maxHeight: 80, overflowY: 'auto' }}>{aiRawText}</div>
                  </details>
                )}
              </>
            ) : (
              !aiError && <div style={{ fontSize: 13, color: '#94a3b8', textAlign: 'center', padding: '4px 0' }}>
                🤔 No serial/product codes detected. Try a wider frame or better lighting.
              </div>
            )}
          </div>
        )}

        {/* Action buttons */}
        <div style={styles.btnRow}>
          <button onClick={onCancel} style={styles.btnCancel}>✕ Cancel</button>
          <button onClick={handleReset} style={styles.btnReset}>↺ Redraw</button>
          {onAIScan && (
            <button onClick={handleAIDetect} disabled={aiLoading || !cameraReady}
              style={{ ...styles.btnAI, opacity: (aiLoading || !cameraReady) ? 0.5 : 1, cursor: (aiLoading || !cameraReady) ? 'not-allowed' : 'pointer' }}>
              {aiLoading ? '⏳' : '🔍'} AI Detect
            </button>
          )}
          <button onClick={() => handleConfirm()} disabled={!canConfirm}
            style={{ ...styles.btnConfirm, opacity: canConfirm ? 1 : 0.4, cursor: canConfirm ? 'pointer' : 'not-allowed' }}>
            ✓ Use This Text
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Utility: rounded rect ───────────────────
function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y); ctx.lineTo(x + w - r, y); ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r); ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h); ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r); ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

// ── Utility: pointer position ────────────────
function getPointerPos(e) {
  if (e.touches && e.touches.length > 0) return { x: e.touches[0].clientX, y: e.touches[0].clientY };
  return { x: e.clientX, y: e.clientY };
}

// ── Styles ──────────────────────────────────
const styles = {
  overlay: { position: 'fixed', inset: 0, zIndex: 99999, background: '#000', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' },
  halfOverlay: { position: 'fixed', left: 0, right: 0, bottom: 0, height: '50dvh', zIndex: 99999, background: '#000', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', borderTopLeftRadius: 16, borderTopRightRadius: 16, overflow: 'hidden', boxShadow: '0 -8px 40px rgba(0,0,0,0.4)', animation: 'slideUpHalf 0.3s ease-out' },
  video: { position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' },
  overlayCanvas: { position: 'absolute', inset: 0, width: '100%', height: '100%', touchAction: 'none', cursor: 'crosshair', zIndex: 2 },
  topBar: { position: 'absolute', top: 0, left: 0, right: 0, padding: '16px 20px', background: 'linear-gradient(to bottom, rgba(0,0,0,0.75), transparent)', zIndex: 3 },
  bottomBar: { position: 'absolute', bottom: 0, left: 0, right: 0, padding: '12px 14px max(16px, env(safe-area-inset-bottom))', background: 'linear-gradient(to top, rgba(0,0,0,0.9), rgba(0,0,0,0.5), transparent)', zIndex: 3, display: 'flex', flexDirection: 'column', gap: 8 },
  textDisplay: { background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(8px)', borderRadius: 10, padding: '10px 14px', border: '1px solid rgba(34,211,238,0.3)' },
  tabBtn: { flex: 1, padding: '6px 10px', borderRadius: 7, border: '1.5px solid', fontSize: 11, fontWeight: 700, cursor: 'pointer', backdropFilter: 'blur(4px)' },
  btnRow: { display: 'flex', gap: 6, justifyContent: 'center', flexWrap: 'wrap' },
  btnCancel: { padding: '10px 16px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.1)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  btnReset: { padding: '10px 16px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.15)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  btnAI: { padding: '10px 18px', borderRadius: 8, border: '1.5px solid #a78bfa', background: 'rgba(167,139,250,0.2)', color: '#a78bfa', fontSize: 13, fontWeight: 700 },
  btnConfirm: { padding: '10px 22px', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg, #06b6d4, #22d3ee)', color: '#0f172a', fontSize: 13, fontWeight: 700, flex: 1, maxWidth: 200 },
  loading: { position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 5, background: 'rgba(0,0,0,0.8)' },
  spinner: { width: 36, height: 36, border: '3px solid rgba(255,255,255,0.2)', borderTopColor: '#22d3ee', borderRadius: '50%', animation: 'liveocrSpin 0.8s linear infinite' },
  errorCard: { padding: '32px 28px', borderRadius: 16, textAlign: 'center', maxWidth: 320, zIndex: 5 },
};

if (typeof document !== 'undefined' && !document.getElementById('liveocrSpin')) {
  const style = document.createElement('style');
  style.id = 'liveocrSpin';
  style.textContent = `
    @keyframes liveocrSpin { to { transform: rotate(360deg) } }
    @keyframes slideUpHalf { from { transform: translateY(100%); } to { transform: translateY(0); } }
  `;
  document.head.appendChild(style);
}

export default LiveOCRScanner;
